import { useEffect, useState } from "react";
import { Flame, Leaf, Clock, Star, Instagram, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api, imgUrl } from "@/lib/api";
import { Reveal, SectionTitle } from "@/components/site/Primitives";

const PILLARS = [
  { icon: Leaf, t: "Her gün taze", d: "Tencerelerimiz her sabah günlük malzemeyle yeniden kurulur." },
  { icon: Flame, t: "Ateş başında ustalık", d: "Izgara ve kokoreçte ustamızın elinden çıkan karakter." },
  { icon: Clock, t: "7/24 açık", d: "Kahvaltıdan gece çorbasına, günün her saati sıcak servis." },
];

export function About() {
  const { settings } = useSite();
  return (
    <section id="hikayemiz" className="bg-[#FAF7F2] py-24 sm:py-32 overflow-hidden" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <Reveal className="lg:col-span-6 relative">
          <div className="relative rounded-lg overflow-hidden border border-[#E5DCD0]">
            <img src="/brand/wordmark.jpg" alt="Yeni Mercan Restoran" className="w-full h-auto" data-testid="about-wordmark-img" />
          </div>
          <div className="hidden sm:block absolute -bottom-10 -right-6 w-56 rounded-lg overflow-hidden border-4 border-[#FAF7F2] shadow-2xl">
            <img src="/images/chef.jpg" alt="Usta" className="h-56 w-full object-cover" />
          </div>
        </Reveal>
        <Reveal className="lg:col-span-6" delay={0.15}>
          <SectionTitle light eyebrow="Hikayemiz" title={settings.about_title || "Buca'ya yeni bir sofra kuruldu"} />
          <p className="mt-6 text-[#6B5E56] leading-relaxed text-base sm:text-lg" data-testid="about-text">{settings.about_text}</p>
          <div className="mt-10 space-y-6">
            {PILLARS.map(({ icon: Icon, t, d }) => (
              <div key={t} className="flex gap-5">
                <div className="h-11 w-11 shrink-0 rounded-full bg-[#1A1513] text-[#D4AF37] grid place-items-center"><Icon size={18} /></div>
                <div>
                  <p className="font-serif-ym text-xl font-semibold text-[#1A1513]">{t}</p>
                  <p className="text-sm text-[#6B5E56] mt-1">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function GalleryGrid() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data));
  }, []);
  if (!items.length) return null;
  return (
    <section id="galeri" className="ym-dark py-24 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><SectionTitle eyebrow="Galeri" title="Sofradan kareler" /></Reveal>
        <div className="mt-14 columns-2 md:columns-3 lg:columns-4 gap-4 [&>*]:mb-4">
          {items.map((g, i) => (
            <button key={g.id} onClick={() => setActive(g)} className="img-zoom group relative block w-full overflow-hidden rounded-lg break-inside-avoid" data-testid={`gallery-item-${i}`}>
              <img src={imgUrl(g.image)} alt={g.title} className={`w-full object-cover ${i % 3 === 0 ? "h-80" : i % 3 === 1 ? "h-56" : "h-64"}`} loading="lazy" />
              <div className="absolute inset-0 bg-[#140D0B]/0 group-hover:bg-[#140D0B]/50 transition-[background-color] duration-500 flex items-end p-4">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 font-serif-ym text-lg">{g.title}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      {active && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-4" onClick={() => setActive(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-white" aria-label="Kapat" data-testid="gallery-lightbox-close"><X size={28} /></button>
          <figure className="max-w-5xl">
            <img src={imgUrl(active.image)} alt={active.title} className="max-h-[80vh] w-auto rounded-lg" />
            <figcaption className="mt-4 text-center font-serif-ym text-xl text-[#FDFBF7]">{active.title}</figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}

export function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/reviews").then(({ data }) => setReviews(data));
  }, []);
  if (!reviews.length) return null;
  return (
    <section className="bg-[#F3EDE2] py-24 sm:py-28" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><SectionTitle light eyebrow="Misafirlerimiz" title="Sofradan kalanlar" /></Reveal>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {reviews.slice(0, 6).map((r, i) => (
            <Reveal key={r.id} delay={i * 0.06}>
              <blockquote className="h-full rounded-lg bg-[#FAF7F2] border border-[#E5DCD0] p-7" data-testid={`review-card-${i}`}>
                <div className="flex gap-1 text-[#D4AF37]">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={14} fill="currentColor" />)}</div>
                <p className="mt-4 font-serif-ym text-xl text-[#1A1513] leading-snug">"{r.text}"</p>
                <footer className="mt-5 text-sm text-[#6B5E56]">— {r.name}</footer>
              </blockquote>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function InstagramCTA() {
  const { settings } = useSite();
  const shots = ["/images/kokorec.jpg", "/images/breakfast.jpg", "/images/antrikot.jpg", "/images/baklava.jpg"];
  return (
    <section className="bg-[#FAF7F2] py-20" data-testid="instagram-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow !text-[#9E7B27]">Instagram</p>
          <h2 className="mt-4 font-serif-ym text-4xl font-semibold text-[#1A1513]">@{settings.instagram}</h2>
          <p className="mt-4 text-[#6B5E56]">Günün tenceresi, yeni tabaklar ve kampanyalar için bizi takip edin.</p>
          <a href={`https://instagram.com/${settings.instagram}`} target="_blank" rel="noopener noreferrer" className="btn-gold mt-8" data-testid="instagram-follow-btn">
            <Instagram size={16} /> Takip Et
          </a>
        </Reveal>
        <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {shots.map((s, i) => (
            <a key={s} href={`https://instagram.com/${settings.instagram}`} target="_blank" rel="noopener noreferrer" className="img-zoom relative aspect-square overflow-hidden rounded-lg" data-testid={`instagram-tile-${i}`}>
              <img src={s} alt="Instagram gönderisi" className="h-full w-full object-cover" loading="lazy" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
